import { timezones } from '@va/assets';
import { validationTranslationKeys } from '@va/constants';
import { Clock } from '@va/icons';
import { useTranslate } from '@va/localization';
import { SelectDropdown, SelectDropdownOption, TextInput } from '@va/ui/design-system';
import { getHowDidYouFindUsOptions } from '@va/util/helpers';
import { useFormik } from 'formik';
import * as yup from 'yup';

export type OnboardingPersonalDetails = {
  firstName: string;
  lastName: string;
  findUs?: SelectDropdownOption<number>;
  timezone?: SelectDropdownOption<string>;
};

const formFieldNames = {
  firstName: 'firstName',
  lastName: 'lastName',
  findUs: 'findUs',
  timezone: 'timezone',
};

export const PersonalDetailsForm: React.FC<{
  formId?: string;
  onSubmit: (values: OnboardingPersonalDetails) => void;
  initialValues?: OnboardingPersonalDetails;
}> = ({ formId, onSubmit, initialValues = { firstName: '', lastName: '' } }) => {
  const translate = useTranslate();
  const validationSchema = useValidationSchema();

  const { handleChange, values, errors, handleSubmit, setFieldValue } = useFormik<OnboardingPersonalDetails>({
    onSubmit,
    initialValues,
    validationSchema,
  });

  return (
    <form id={formId} className='max-w-600px mx-auto flex flex-col gap-3' onSubmit={handleSubmit}>
      <div className='flex gap-3'>
        <TextInput
          wrapperClassName='flex-1'
          onChange={handleChange}
          value={values.firstName}
          error={errors.firstName}
          name={formFieldNames.firstName}
          label={translate('onboarding.standalone.personalDetails.firstNameField')}
          clearField={() => {
            setFieldValue(formFieldNames.firstName, '');
          }}
          required
        />
        <TextInput
          wrapperClassName='flex-1'
          value={values.lastName}
          error={errors.lastName}
          onChange={handleChange}
          name={formFieldNames.lastName}
          label={translate('onboarding.standalone.personalDetails.lastNameField')}
          clearField={() => {
            setFieldValue(formFieldNames.lastName, '');
          }}
          required
        />
      </div>
      <SelectDropdown
        size='medium'
        buttonClassName='!rounded-12'
        selectedOption={values.findUs}
        dropdownOptions={getHowDidYouFindUsOptions(translate)}
        onChange={(value) => setFieldValue(formFieldNames.findUs, value)}
        placeholderText={translate('onboarding.standalone.personalDetails.howDidYouFindUs.placeholder')}
      />
      <SelectDropdown
        size='medium'
        dropdownOptions={timezones}
        buttonClassName='!rounded-12'
        selectedOption={values.timezone}
        icon={<Clock strokeColor='#696969' />}
        onChange={(value) => setFieldValue(formFieldNames.timezone, value)}
        placeholderText={translate('onboarding.standalone.personalDetails.timezone.placeholder')}
      />
    </form>
  );
};

const useValidationSchema = () => {
  const translate = useTranslate();

  return yup.object().shape({
    firstName: yup.string().required(translate(validationTranslationKeys.required)),
    lastName: yup.string().required(translate(validationTranslationKeys.required)),
  });
};
